import { Button, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import EvStationPropType from '../../prop-types/evStation';
import styles from './EvStationPopup.module.css';

const cx = classnames.bind(styles);

/**
 * Get charging level display text based on station's charger types
 */
function getChargingLevel(station) {
  if (station.evDcFastNum > 0) {
    return 'DC Fast Charging';
  }
  if (station.evLevel2EvseNum > 0) {
    return 'Level 2';
  }
  if (station.evLevel1EvseNum > 0) {
    return 'Level 1';
  }
  return null;
}

/**
 * Format the last confirmed date for display
 */
function formatLastConfirmed(dateStr) {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * EvStationPopup - Popup card displayed above map markers
 *
 * Shows station info: name, address, distance, hours, access, charging level,
 * disclaimer, and action buttons for directions and phone.
 */
export default function EvStationPopup({
  station,
  onClose,
  onDirectionsClick,
  onPhoneClick,
  onWebsiteClick,
}) {
  if (!station) {
    return null;
  }

  const chargingLevel = getChargingLevel(station);
  const totalPorts = (station.evLevel1EvseNum || 0)
    + (station.evLevel2EvseNum || 0)
    + (station.evDcFastNum || 0);
  const lastConfirmed = formatLastConfirmed(station.dateLastConfirmed);

  const handleDirections = () => {
    if (onDirectionsClick) {
      onDirectionsClick(station);
    }
    // Default: open Google Maps directions
    const { streetAddress, city, state, zip } = station;
    const destination = encodeURIComponent(`${streetAddress}, ${city}, ${state} ${zip}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=&travelmode=driving`, '_blank');
  };

  const handlePhone = () => {
    if (onPhoneClick) {
      onPhoneClick(station);
    }
    if (station.stationPhone) {
      window.location.href = `tel:${station.stationPhone}`;
    }
  };

  const handleWebsite = () => {
    if (onWebsiteClick) {
      onWebsiteClick(station);
    }
    if (station.evNetworkWeb) {
      window.open(station.evNetworkWeb, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={cx('popup')}>
      {/* Close button */}
      <button
        type="button"
        className={cx('closeButton')}
        onClick={onClose}
        aria-label="Close"
      >
        &times;
      </button>

      {/* Station name and network */}
      <div className={cx('header')}>
        <Text as="h3" className={cx('stationName')}>
          {station.stationName}
        </Text>
        {station.evNetwork && (
          <Text as="span" className={cx('network')}>
            {station.evNetwork}
          </Text>
        )}
      </div>

      {/* Address and distance */}
      <div className={cx('addressRow')}>
        <Text as="p" className={cx('address')}>
          {station.streetAddress}, {station.city}, {station.state} {station.zip}
        </Text>
        {station.distanceMiles != null && (
          <Text as="span" className={cx('distance')}>
            {station.distanceMiles.toFixed(1)} mi
          </Text>
        )}
      </div>

      {/* Hours */}
      {station.accessDaysTime && (
        <div className={cx('infoRow')}>
          <Text as="span" className={cx('infoLabel')}>Hours:</Text>
          <Text as="span" className={cx('infoValue')}>{station.accessDaysTime}</Text>
        </div>
      )}

      {/* Access */}
      {station.accessCode && (
        <div className={cx('infoRow')}>
          <Text as="span" className={cx('infoLabel')}>Access:</Text>
          <Text as="span" className={cx('infoValue')}>
            {station.accessCode === 'public' ? 'Public' : station.accessCode}
          </Text>
        </div>
      )}

      {/* Charging info row */}
      <div className={cx('chargingRow')}>
        {chargingLevel && (
          <span className={cx('chargingLevel', {
            dcFast: station.evDcFastNum > 0,
            level2: station.evDcFastNum === 0 && station.evLevel2EvseNum > 0,
          })}
          >
            {chargingLevel}
          </span>
        )}
        {totalPorts > 0 && (
          <span className={cx('portCount')}>
            {totalPorts} port{totalPorts !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Action buttons - using standard Button variants */}
      <div className={cx('actions')}>
        <Button
          size="sm"
          className={cx('actionButton')}
          onClick={handleDirections}
          secondary
        >
          Directions
        </Button>
        {station.stationPhone && (
          <Button
            size="sm"
            className={cx('actionButton')}
            onClick={handlePhone}
            secondary
          >
            Call
          </Button>
        )}
        {station.evNetworkWeb && (
          <Button
            size="sm"
            className={cx('actionButton')}
            onClick={handleWebsite}
            secondary
          >
            Website
          </Button>
        )}
      </div>

      {/* Last confirmed and disclaimer */}
      <div className={cx('footer')}>
        {lastConfirmed && (
          <Text as="p" className={cx('lastConfirmed')}>
            Last confirmed: {lastConfirmed}
          </Text>
        )}
        <Text as="p" className={cx('disclaimer')}>
          Data provided by NREL. Station availability and details may vary.
          Please verify before visiting.
        </Text>
      </div>

      {/* Popup arrow pointing down to marker */}
      <div className={cx('arrow')} />
    </div>
  );
}

EvStationPopup.propTypes = {
  station: EvStationPropType,
  onClose: PropTypes.func.isRequired,
  onDirectionsClick: PropTypes.func,
  onPhoneClick: PropTypes.func,
  onWebsiteClick: PropTypes.func,
};

EvStationPopup.defaultProps = {
  station: null,
  onDirectionsClick: undefined,
  onPhoneClick: undefined,
  onWebsiteClick: undefined,
};
